<template>
  <div class="permission-management">
    <div class="page-header">
      <h2>权限管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增权限</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="权限名称">
        <el-input v-model="searchForm.permission_name" placeholder="请输入权限名称" clearable />
      </el-form-item>
      <el-form-item label="权限类型">
        <el-select v-model="searchForm.permission_type" placeholder="请选择权限类型" clearable>
          <el-option label="菜单" value="menu" />
          <el-option label="按钮" value="button" />
          <el-option label="接口" value="api" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="禁用" :value="0" />
          <el-option label="启用" :value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="permissionList" border row-key="id" :tree-props="{ children: 'children', hasChildren: 'hasChildren' }" style="width: 100%">
      <el-table-column prop="permission_name" label="权限名称" width="200">
        <template slot-scope="scope">
          <span :style="{ paddingLeft: (scope.row.level * 20) + 'px' }">
            <i :class="getPermissionIcon(scope.row.permission_type)"></i>
            {{ scope.row.permission_name }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="permission_code" label="权限编码" />
      <el-table-column prop="permission_type" label="权限类型" width="100">
        <template slot-scope="scope">
          <el-tag :type="getPermissionTypeTag(scope.row.permission_type)">
            {{ getPermissionTypeText(scope.row.permission_type) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="path" label="路径" />
      <el-table-column prop="icon" label="图标" width="80">
        <template slot-scope="scope">
          <i v-if="scope.row.icon" :class="scope.row.icon"></i>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="sort_order" label="排序" width="80" />
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
            {{ scope.row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="270" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="viewPermission(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="editPermission(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="success-btn" @click="addChildPermission(scope.row)">添加子权限</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="deletePermission(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 权限表单对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form ref="permissionForm" :model="permissionForm" :rules="permissionRules" label-width="100px">
        <el-form-item label="上级权限" prop="parent_id">
          <el-cascader
            v-model="permissionForm.parent_id"
            :options="permissionOptions"
            :props="{ value: 'id', label: 'permission_name', children: 'children', checkStrictly: true }"
            placeholder="请选择上级权限（不选择为顶级权限）"
            clearable
          />
        </el-form-item>
        <el-form-item label="权限名称" prop="permission_name">
          <el-input v-model="permissionForm.permission_name" />
        </el-form-item>
        <el-form-item label="权限编码" prop="permission_code">
          <el-input v-model="permissionForm.permission_code" />
        </el-form-item>
        <el-form-item label="权限类型" prop="permission_type">
          <el-select v-model="permissionForm.permission_type" placeholder="请选择权限类型">
            <el-option label="菜单" value="menu" />
            <el-option label="按钮" value="button" />
            <el-option label="接口" value="api" />
          </el-select>
        </el-form-item>
        <el-form-item label="路径" prop="path">
          <el-input v-model="permissionForm.path" placeholder="前端路由路径或API接口路径" />
        </el-form-item>
        <el-form-item label="图标" prop="icon">
          <el-input v-model="permissionForm.icon" placeholder="Element UI图标类名">
            <template slot="prepend">
              <i v-if="permissionForm.icon" :class="permissionForm.icon"></i>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="permissionForm.sort_order" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="permissionForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="permissionForm.remark" type="textarea" rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePermission">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getPermissionList, createPermission, updatePermission, deletePermission } from '../../api/system'

export default {
  name: 'PermissionManagement',
  data() {
    return {
      loading: false,
      permissionList: [],
      permissionOptions: [],
      searchForm: {
        permission_name: '',
        permission_type: '',
        status: ''
      },
      dialogVisible: false,
      dialogTitle: '新增权限',
      isEdit: false,
      permissionForm: {
        id: null,
        parent_id: null,
        permission_name: '',
        permission_code: '',
        permission_type: 'menu',
        path: '',
        icon: '',
        sort_order: 0,
        status: 1,
        remark: ''
      },
      permissionRules: {
        permission_name: [
          { required: true, message: '请输入权限名称', trigger: 'blur' }
        ],
        permission_code: [
          { required: true, message: '请输入权限编码', trigger: 'blur' }
        ],
        permission_type: [
          { required: true, message: '请选择权限类型', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.fetchPermissionList()
  },
  methods: {
    async fetchPermissionList() {
      this.loading = true
      try {
        const params = {
          ...this.searchForm
        }
        const response = await getPermissionList(params)
        if (response.code === 200) {
          this.permissionList = this.buildPermissionTree(response.data.list)
          this.permissionOptions = [{ id: 0, permission_name: '顶级权限', children: this.buildPermissionOptions(response.data.list) }]
        }
      } catch (error) {
        this.$message.error('获取权限列表失败')
      } finally {
        this.loading = false
      }
    },
    buildPermissionTree(data, level = 0) {
      return data.map(item => ({
        ...item,
        level: level,
        children: item.children ? this.buildPermissionTree(item.children, level + 1) : []
      }))
    },
    buildPermissionOptions(data) {
      return data.map(item => ({
        id: item.id,
        permission_name: item.permission_name,
        children: item.children ? this.buildPermissionOptions(item.children) : []
      }))
    },
    handleSearch() {
      this.fetchPermissionList()
    },
    handleReset() {
      this.searchForm = {
        permission_name: '',
        permission_type: '',
        status: ''
      }
      this.fetchPermissionList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增权限'
      this.isEdit = false
      this.permissionForm = {
        id: null,
        parent_id: null,
        permission_name: '',
        permission_code: '',
        permission_type: 'menu',
        path: '',
        icon: '',
        sort_order: 0,
        status: 1,
        remark: ''
      }
      this.dialogVisible = true
    },
    viewPermission(row) {
      // 查看权限详情
      this.$message.info('查看功能待实现')
    },
    editPermission(row) {
      this.dialogTitle = '编辑权限'
      this.isEdit = true
      this.permissionForm = { ...row }
      this.dialogVisible = true
    },
    addChildPermission(row) {
      this.dialogTitle = '添加子权限'
      this.isEdit = false
      this.permissionForm = {
        id: null,
        parent_id: row.id,
        permission_name: '',
        permission_code: '',
        permission_type: 'button',
        path: '',
        icon: '',
        sort_order: 0,
        status: 1,
        remark: ''
      }
      this.dialogVisible = true
    },
    async savePermission() {
      try {
        await this.$refs.permissionForm.validate()
        let response
        if (this.isEdit) {
          response = await updatePermission(this.permissionForm)
        } else {
          response = await createPermission(this.permissionForm)
        }
        
        if (response.code === 200) {
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.fetchPermissionList()
        } else {
          this.$message.error(response.msg || '操作失败')
        }
      } catch (error) {
        console.error('保存权限失败', error)
      }
    },
    async deletePermission(row) {
      // 检查是否有子权限
      if (row.children && row.children.length > 0) {
        this.$message.warning('该权限下存在子权限，请先删除子权限')
        return
      }
      
      try {
        await this.$confirm('确定要删除该权限吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await deletePermission({ id: row.id })
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.fetchPermissionList()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    getPermissionIcon(type) {
      const icons = {
        menu: 'el-icon-folder',
        button: 'el-icon-thumb',
        api: 'el-icon-connection'
      }
      return icons[type] || 'el-icon-document'
    },
    getPermissionTypeTag(type) {
      const tags = {
        menu: 'primary',
        button: 'success',
        api: 'warning'
      }
      return tags[type] || 'info'
    },
    getPermissionTypeText(type) {
      const texts = {
        menu: '菜单',
        button: '按钮',
        api: '接口'
      }
      return texts[type] || type
    }
  }
}
</script>

<style scoped>
.permission-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.table-operations {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .success-btn {
  color: #67c23a;
}

.table-operations .success-btn:hover {
  color: #85ce61;
  background-color: #f0f9eb;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}
</style>